import React from "react";
import {
  FaRegClone,
  FaRegPlusSquare,
  FaRegMinusSquare,
  FaCog,
  FaUndo,
  FaRedo,
} from "react-icons/fa";
import chroma from "chroma-js";

export default class LayerGUI extends React.Component {
  constructor(props) {
    super(props);
    this.turns = 0;
    this.layerImages = {};
    this.state = {
      draggedLayer: null,
      dragOverIndex: null,
    };
  }

  createLayerImageUrl = (layer) => {
    if (layer.playing && this.layerImages[layer.id])
      return this.layerImages[layer.id];

    const minRadius = 250;
    const maxRadius = 700;

    const maxDisplay = Math.round(maxRadius / 10);
    const active = layer.frame;
    const frames = layer.frames.length;

    const radius = Math.min(
      maxRadius,
      minRadius + ((maxRadius - minRadius) * frames) / 50,
    );

    const c = document.createElement("canvas");
    c.width = c.height = radius * 2;
    c.style.cssText = "height:500px;width:500px;";
    const ctx = c.getContext("2d");
    ctx.translate(radius, radius);

    const inc = (2 * Math.PI) / Math.min(maxDisplay, frames);
    const a = 2 * Math.PI * frames;

    // Calculate contrasting color for n-gon
    const contrastColor =
      chroma(layer.color).luminance() > 0.5 ? "#000000" : "#ffffff";

    let last = [0, 0];
    ctx.lineWidth = layer.active ? 6 : 4;
    ctx.fillStyle = contrastColor;
    ctx.strokeStyle = contrastColor;

    for (var t = 0, i = 0; t < 2 * Math.PI; t += inc, i++) {
      const x = Math.cos(t);
      const y = Math.sin(t);

      ctx.beginPath();
      if (i) ctx.moveTo(...last);
      ctx.lineTo(radius * x, radius * y);
      if (t + inc + 10e-3 > 2 * Math.PI) ctx.lineTo(radius, 0);
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(
        (radius - 80) * x,
        (radius - 80) * y,
        frames - i === active || (!i && !active) ? 30 : 25,
        0,
        2 * Math.PI,
      );
      ctx[
        layer.frames[i === 0 ? 0 : frames - i].paths.length ? "fill" : "stroke"
      ]();
      last = [radius * x, radius * y];
    }

    ctx.fillStyle = contrastColor;
    ctx.fillText(`${active}`, 0, -15);
    ctx.fillText(`${frames}`, 0, 15);

    return (this.layerImages[layer.id] = [c.toDataURL(), radius]);
  };

  getLayerImage = (layer) => {
    let [url, size] = this.createLayerImageUrl(layer);
    // if (!url) url = this.layerImages[layer.id] = this.createLayerImageUrl(layer);
    return (
      <img
        style={{
          transform: `rotate(${(layer.cycles + layer.frame / layer.frames.length).toPrecision(4)}turn)`,
        }}
        src={url}
        width={(size * 80) / size}
        height={(size * 80) / size}
      />
    );
  };

  handleDragStart = (e, layer, index) => {
    e.stopPropagation();
    this.setState({ draggedLayer: layer });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.currentTarget);
  };

  handleDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (this.state.dragOverIndex !== index) {
      this.setState({ dragOverIndex: index });
    }
    e.dataTransfer.dropEffect = "move";
    return false;
  };

  handleDragLeave = (e) => {
    e.stopPropagation();
  };

  handleDrop = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const { draggedLayer } = this.state;
    const { onMoveLayer } = this.props;

    if (draggedLayer && onMoveLayer) {
      onMoveLayer(draggedLayer, targetIndex);
    }

    this.setState({ draggedLayer: null, dragOverIndex: null });
    return false;
  };

  handleDragEnd = (e) => {
    this.setState({ draggedLayer: null, dragOverIndex: null });
  };

  render() {
    const {
      activeLayer,
      layers,
      onAddFrame,
      onRemoveFrame,
      onAddLayer,
      onSetLayerFPS,
      onRemoveLayer,
      onDuplicateLayer,
      onSelectLayer,
      onShowSettings,
      onUndo,
      onRedo,
    } = this.props;
    return (
      <aside className={activeLayer && !activeLayer.drawing ? "active" : ""}>
        <header>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onShowSettings();
            }}
            title="Settings"
          >
            <FaCog />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddLayer();
            }}
            title="Add Layer"
          >
            <FaRegPlusSquare />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemoveLayer();
            }}
            title="Remove Layer"
          >
            <FaRegMinusSquare />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicateLayer();
            }}
            title="Duplicate Layer"
          >
            <FaRegClone />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onUndo();
            }}
            title="Undo"
          >
            <FaUndo />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRedo();
            }}
            title="Redo"
          >
            <FaRedo />
          </button>
        </header>
        <div>
          {layers.map((layer, i) => (
            <div
              key={layer.id}
              draggable
              onClick={() => onSelectLayer(layer)}
              onDragStart={(e) => this.handleDragStart(e, layer, i)}
              onDragOver={(e) => this.handleDragOver(e, i)}
              onDragLeave={this.handleDragLeave}
              onDrop={(e) => this.handleDrop(e, i)}
              onDragEnd={this.handleDragEnd}
              className={`${layer.active ? "active" : ""} ${this.state.draggedLayer?.id === layer.id ? "dragging" : ""} ${this.state.dragOverIndex === i ? "drag-over" : ""}`}
              style={{
                backgroundColor: layer.color,
                "--contrast-color":
                  chroma(layer.color).luminance() > 0.5 ? "#000000" : "#ffffff",
              }}
            >
              <div>
                <div>{this.getLayerImage(layer)}</div>
                <div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddFrame();
                    }}
                    data-tooltip="Add frame"
                  >
                    +
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveFrame();
                    }}
                    data-tooltip="Remove frame"
                  >
                    -
                  </button>
                </div>
              </div>
              <div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetLayerFPS(Math.max(1, layer.fps - 1), layer);
                  }}
                  data-tooltip="Decrease FPS"
                >
                  ‹
                </button>
                <input
                  name="fps"
                  type="number"
                  step="1"
                  min="1"
                  max="60"
                  value={layer.fps}
                  onChange={(e) =>
                    onSetLayerFPS(parseInt(e.target.value, 10), layer)
                  }
                  title="FPS"
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetLayerFPS(Math.min(60, layer.fps + 1), layer);
                  }}
                  data-tooltip="Increase FPS"
                >
                  ›
                </button>
              </div>
            </div>
          ))}
        </div>
      </aside>
    );
  }
}
